import { StatusCode } from "components/http/ajax/statusCode";
import { EndpointResponse } from "components/http/endpoint";
import { EntityState } from "components/state/entity";
import { Container, Dictionary } from "../state/types";
import { toSingleString, Strings } from "./strings";

interface FieldErrors {
    errors: Strings,
    evaluate?: boolean
}
const NoFieldError: FieldErrors = {
    errors: null,
    evaluate: null
}

type EntityErrors = Dictionary<FieldErrors>;
const NoEntityError: EntityErrors = {
}

const Entity = "";

interface EntityResponse<T> {
    entity?: T;
    errors?: EntityErrors;
}

interface CollectionResponse<T> {
    collection?: T[];
    errors?: EntityErrors;
}

interface ValidatableEntity<Entity> {
    entity?: Entity,
    collection?: Entity[],
    errors: EntityErrors
}

function setEnableFieldValidation(
    errors: EntityErrors,
    fieldName: string,
    enabled: boolean) {
    let property = errors[fieldName];
    if (property != null) {
        property.evaluate = enabled;
    }
}

function isFieldErrorsValid(field: FieldErrors): boolean {
    if (field.evaluate === false) {
        return true;
    }

    const messages = field.errors;
    if (!Array.isArray(messages)) {
        return messages === null;
    } else if (messages.length === 0) {
        return true;
    } else if (messages.length === 1) {
        return messages[0] === null;
    } else {
        return false;
    }
}

function isFieldValid<E>(state: EntityState<E>, field: string): boolean {
    const errors = state.errors[field];
    return errors == null ?
        true :
        isFieldErrorsValid(errors);
}

function getFieldErrors<E>(state: EntityState<E>, field: string): Strings {
    return state.errors[field]?.errors;
}

function getFieldErrorsAsString<E>(state: EntityState<E>, field: string): string {
    return toSingleString(state.errors[field]?.errors);
}

function isEntityErrorsValid(errors: EntityErrors): boolean {
    return Object.entries(errors).every(
        item => isFieldErrorsValid(item[1])
    );
}

function isEntityValid<E>(state: EntityState<E>): boolean {
    return isEntityErrorsValid(state.errors);
}

function getEntityErrors<E>(state: EntityState<E>): Strings {
    return state.errors[Entity]?.errors;
}

function updateByResponse<E>(draft: EntityState<E>, response: EndpointResponse) {
    if (response.succeeded) {
        draft.entity = response.data as E;
        clearErrors(draft);
    } else {
        updateErrorsFromResponse(draft, response);
    }
}

function updateByEntityResponse<E>(draft: EntityState<E>, response: EndpointResponse) {

    const entityResponse = response.data as EntityResponse<E>;
    if (response.succeeded) {
        draft.entity = entityResponse.entity;
        clearErrors(draft);
    }

    if (entityResponse != null && entityResponse.errors != null) {
        validationErrorsToEntityErrors(draft, entityResponse.errors, true);
    } else if (!response.succeeded) {
        responseErrorToEntityError(draft, response.status, true);
    }
}

function updateByCollectionResponse<E>(draft: EntityState<E>, response: EndpointResponse) {

    const colectionResponse = response.data as CollectionResponse<E>;
    if (response.succeeded) {
        draft.collection = colectionResponse.collection;
        clearErrors(draft);
    }

    if (colectionResponse != null && colectionResponse.errors != null) {
        validationErrorsToEntityErrors(draft, colectionResponse.errors, true);
    } else if (!response.succeeded) {
        responseErrorToEntityError(draft, response.status, true);
    }
}

function updateErrorsFromResponse<E>(draft: EntityState<E>, response: EndpointResponse) {
    if (response.succeeded) {
        clearErrors(draft);
        return;
    }

    const data = response.data;
    if (data == null || data["ValidationErrors"] == null) {
        responseErrorToEntityError(draft, response.status, true);
    } else {
        validationErrorsToEntityErrors(draft, data["ValidationErrors"], true);
    }
}

function clearErrors<E>(draft: EntityState<E>) {
    draft.errors = { ...NoEntityError };
}

function responseErrorToEntityError<E>(draft: EntityState<E>, status: StatusCode, clearExisting: boolean) {
    let message
    switch (status) {
        case StatusCode.Unauthorized:
            message = "Nemáte oprávnění k provedení akce.";
            break;
        case StatusCode.NotFound:
            message = "Zdroj nenalezen.";
            break;
        case StatusCode.BadRequest:
            message = "Chybný požadavek.";
            break;
        case StatusCode.InternalServerError:
            message = "Interní chyba serveru.";
            break;
        default:
            message = status.toString(); // TODO something user friendly
            break;
    }
    errorToEntityError(draft, message, clearExisting);
}


function errorToEntityError<E>(draft: EntityState<E>, message: string, clearExisting: boolean, entityField: string = Entity) {
    let errors = clearExisting ?
        { ...NoEntityError } :
        draft.errors;

    errors[entityField] = {
        errors: message,
        evaluate: null
    }

    draft.errors = errors;
}



function validationErrorsToEntityErrors<E>(draft: EntityState<E>, validationErrors: Container, clearExisting: boolean) {
    let errors = clearExisting ?
        { ...NoEntityError } :
        draft.errors;

    for (const validationError of Object.entries(validationErrors)) {
        errors[validationError[0]] = {
            errors: validationError[1],
            evaluate: null
        };
    }

    draft.errors = errors;
}

export {
    Entity as WholeEntityError,
    EntityErrors,
    EntityResponse,
    CollectionResponse,
    errorToEntityError,
    FieldErrors,
    getEntityErrors,
    getFieldErrors,
    getFieldErrorsAsString,
    isEntityErrorsValid,
    isEntityValid,
    isFieldErrorsValid,
    isFieldValid,
    NoEntityError,
    NoFieldError,
    setEnableFieldValidation,
    updateByEntityResponse,
    updateByCollectionResponse,
    updateByResponse,
    updateErrorsFromResponse,
    ValidatableEntity
}